import { assert, mapFilterUndefined } from "@glideapps/ts-necessities";
import startCase from "lodash/startCase";
import type { ArgumentSpec } from "../common/components/computation-types";
import type { LoadedGroundValue } from "../common/computation-model/data";
import { asBoolean, asMaybeString, asString, isPrimitive } from "../common/computation-model/data";
import type {
    ArrayContentDescription,
    ColumnType,
    ColumnTypeKind,
    ComponentDescription,
    Description,
    MutatingScreenKind,
    PropertyDescription,
    StringGlideTypeKind,
} from "../common/description";
import { PropertyKind } from "../common/description";
import type {
    ActionPropertyDescriptor,
    EnumPropertyCase,
    EnumVisual,
    PropertyDescriptor,
    PropertyTableGetter,
    SchemaInspector,
    Subcomponent,
} from "../component-utils";
import { ArrayPropertyStyle } from "../component-utils";
import { phoneProperties, titleProperties } from "../component-utils/description-utils";
import { makeImagePropertyDescriptor, makeTextPropertyDescriptor } from "../component-utils/descriptor-utils";
import { getDefaultPrimitiveActionKinds } from "../component-utils/primitive-actions";
import { EnumPropertyHandler, SwitchPropertyHandler } from "../component-utils/property-handlers";
import type { SuperPropertySection } from "../component-utils/property-sections";
import { PropertySection } from "../component-utils/property-sections";
import { getEmojiProperty } from "../component-utils/property-utils";
import type { WireValueGetter, WireValueInflationBackend } from "../wire/backends";
import type { Unbound, WireAction, WirePrimitiveValueWithType } from "../wire/types";

export type ComponentOrArrayDescription = ComponentDescription | ArrayContentDescription;

type PropertyDescriptorConstructor = (
    desc: ComponentOrArrayDescription | undefined,
    parentDesc: ComponentOrArrayDescription | undefined,
    getPropertyTable: PropertyTableGetter | undefined,
    schema: SchemaInspector | undefined,
    mutatingScreenKind: MutatingScreenKind | undefined,
    isEditedInApp: boolean,
    invalidateForNewTable: boolean
) => PropertyDescriptor | undefined;

type WithEnumPropertyValuesConvenient<TEnum extends string> = readonly TEnum[] | EnumPropertyCase<TEnum>[];

function finalizeWithEnumPropertyValues<TEnum extends string>(
    values: WithEnumPropertyValuesConvenient<TEnum>
): EnumPropertyCase<TEnum>[] {
    if (typeof values[0] === "string") {
        return (values as TEnum[]).map(value => ({ value, label: startCase(value) }));
    }
    return values as EnumPropertyCase<TEnum>[];
}

type ToPropertyDescriptionOrUndefined<T> = { readonly [K in keyof T]: PropertyDescription | undefined };
export type DescriptionOfProperties<T> = T extends FluentProperties<infer U, unknown>
    ? ToPropertyDescriptionOrUndefined<U>
    : never;

interface PropertyOptionsWithoutPreferredNames {
    readonly displayName: string;
    readonly required: boolean;
    readonly emptyByDefault?: boolean;
    readonly propertySection?: SuperPropertySection;
    readonly subcomponent?: Subcomponent;
}

interface PropertyOptions extends PropertyOptionsWithoutPreferredNames {
    readonly preferredNames: readonly string[];
}

type WhenPredicate<T, TParent> = (
    desc: ToPropertyDescriptionOrUndefined<T>,
    parentDesc: ToPropertyDescriptionOrUndefined<TParent> | undefined
) => boolean;

interface WhenOption<T, TParent> {
    readonly when?: WhenPredicate<T, TParent>;
}

interface ConstructableSpec {
    readonly descriptorConstructor: PropertyDescriptorConstructor;
    readonly when: WhenPredicate<ComponentOrArrayDescription, ComponentOrArrayDescription> | undefined;
}

interface SpecBase extends ConstructableSpec {
    readonly argumentSpec: ArgumentSpec;
}

type ValueConverter = (v: LoadedGroundValue) => LoadedGroundValue;

// Returns [value getter, column type, value is displayed]
//
// When a value that's "displayed" is not empty, the component must be drawn.
// For example, the style of a Title component is not displayed because a
// Title that has nothing but a style is still empty.  It's image is
// displayed, however.
type ValueGetterGetter = (
    desc: Description,
    ib: WireValueInflationBackend
) => readonly [getter: WireValueGetter, type: ColumnType, isDisplayed: boolean];

interface PropertySpec extends SpecBase {
    readonly isSearchable: boolean;
    readonly emptyByDefault: boolean;
    readonly withType: boolean;
    readonly getValueGetter: ValueGetterGetter;
    readonly converter: ValueConverter;
    readonly preferredType: ColumnTypeKind | undefined;
}

interface ArraySpec extends ConstructableSpec {
    readonly name: string;
    readonly itemsSpec: FluentPropertiesSpec;
    // If the key property is missing, we also remove all the value
    // properties.  We use this in action-with-title arrays to clear the title
    // when there's no action, so it doesn't render as a disabled button.
    readonly removeIfMissing: Record<string, readonly string[]>;
}

export interface ActionSpec extends SpecBase {}

export interface FluentPropertiesSpec {
    readonly propertySpecs: readonly PropertySpec[];
    readonly arraySpecs: readonly ArraySpec[];
    readonly actionSpecs: readonly ActionSpec[];
    readonly hasDisplayedProperties: boolean;
}

function ensurePropertyOptions(propertyName: string, opts: Partial<PropertyOptions>): PropertyOptions {
    const { displayName = startCase(propertyName), required = false, emptyByDefault } = opts;
    let preferredNames: readonly string[];
    if (opts.preferredNames !== undefined) {
        preferredNames = opts.preferredNames;
    } else if (propertyName === "title" || propertyName === "subtitle") {
        preferredNames = titleProperties;
    } else if (propertyName === "phone") {
        preferredNames = phoneProperties;
    } else {
        preferredNames = [propertyName.toLowerCase()];
    }
    return {
        // We need to keep all original properties because `when` might be in
        // there.
        ...opts,
        displayName,
        preferredNames,
        required,
        emptyByDefault,
        propertySection: opts.propertySection,
    };
}

export function makePropertyDescriptors(
    desc: ComponentOrArrayDescription | undefined,
    parentDesc: ComponentOrArrayDescription | undefined,
    specs: readonly ConstructableSpec[],
    getPropertyTable: PropertyTableGetter | undefined,
    schema: SchemaInspector | undefined,
    mutatingScreenKind: MutatingScreenKind | undefined,
    isEditedInApp: boolean,
    invalidateForNewTable: boolean
): PropertyDescriptor[] {
    return mapFilterUndefined(specs, p =>
        p.descriptorConstructor(
            desc,
            parentDesc,
            getPropertyTable,
            schema,
            mutatingScreenKind,
            isEditedInApp,
            invalidateForNewTable
        )
    );
}

export function makeActionDescriptors(
    desc: ComponentOrArrayDescription | undefined,
    parentDesc: ComponentOrArrayDescription | undefined,
    specs: readonly ConstructableSpec[],
    getPropertyTable: PropertyTableGetter | undefined,
    schema: SchemaInspector | undefined,
    mutatingScreenKind: MutatingScreenKind | undefined,
    invalidateForNewTable: boolean
): ActionPropertyDescriptor[] {
    return makePropertyDescriptors(
        desc,
        parentDesc,
        specs,
        getPropertyTable,
        schema,
        mutatingScreenKind,
        false,
        invalidateForNewTable
    ) as ActionPropertyDescriptor[];
}

export class FluentProperties<TProps, TParent> {
    constructor(public readonly spec: FluentPropertiesSpec) {}

    public withParent<T>(): FluentProperties<TProps, T> {
        // Ugh.  The alternative to this would be to always construct it with
        // the parent type, but then how do we make sure that the `spec`
        // (which would have to be optional in that case) is of the correct
        // type?
        assert(
            this.spec.propertySpecs.length === 0 &&
                this.spec.arraySpecs.length === 0 &&
                this.spec.actionSpecs.length === 0
        );
        return this as FluentProperties<TProps, T>;
    }

    private withArgument<T>(
        argumentSpec: ArgumentSpec,
        descriptorConstructor: PropertyDescriptorConstructor,
        isSearchable: boolean,
        emptyByDefault: boolean,
        withType: boolean,
        getValueGetter: ValueGetterGetter,
        converter: ValueConverter,
        preferredType: ColumnTypeKind | undefined,
        isDisplayed: boolean,
        when?: WhenPredicate<TProps, TParent>
    ): FluentProperties<T, TParent> {
        let c = descriptorConstructor;
        if (when !== undefined) {
            c = (desc, parentDesc, ...args) => {
                if (desc === undefined || !when(desc as any, parentDesc as any)) return undefined;
                return descriptorConstructor(desc, parentDesc, ...args);
            };
        }
        return new FluentProperties({
            ...this.spec,
            propertySpecs: [
                ...this.spec.propertySpecs,
                {
                    descriptorConstructor: c,
                    when: when as WhenPredicate<ComponentOrArrayDescription, ComponentOrArrayDescription> | undefined,
                    argumentSpec,
                    isSearchable,
                    emptyByDefault,
                    withType,
                    getValueGetter,
                    converter,
                    preferredType,
                },
            ],
            hasDisplayedProperties: this.spec.hasDisplayedProperties || isDisplayed,
        });
    }

    private makeDefaultValueGetterGetter(name: string, withFormat: boolean, isDisplayed: boolean): ValueGetterGetter {
        return (desc, ib) => [...ib.getValueGetterForProperty((desc as any)[name], withFormat), isDisplayed];
    }

    private withPrimitiveProperty<T extends string, V>(
        name: T,
        opts: Partial<PropertyOptions & WhenOption<TProps, TParent>>,
        {
            isSearchable,
            withFormat,
            isDisplayed,
            withType,
            preferredType,
        }: {
            isSearchable: boolean;
            withFormat: boolean;
            isDisplayed: boolean;
            withType: boolean;
            preferredType: ColumnTypeKind | undefined;
        },
        propertyDescriptorConstructor: PropertyDescriptorConstructor
    ): FluentProperties<TProps & Partial<Record<T, V | Unbound | undefined>>, TParent> {
        const { required } = ensurePropertyOptions(name, opts);
        const argumentSpec: ArgumentSpec = [name, "evaluable", required];
        return this.withArgument(
            argumentSpec,
            propertyDescriptorConstructor,
            isSearchable,
            opts.emptyByDefault === true,
            withType,
            this.makeDefaultValueGetterGetter(name, withFormat, isDisplayed),
            withType ? x => (isPrimitive(x) ? x : undefined) : asMaybeString,
            preferredType,
            isDisplayed,
            opts.when
        );
    }

    public withPrimitive<T extends string>(
        name: T,
        opts: Partial<
            PropertyOptions &
                WhenOption<TProps, TParent> & {
                    readonly allowColumn: boolean;
                    readonly isDefaultCaption: boolean;
                }
        > = {}
    ) {
        const propertyOptions = ensurePropertyOptions(name, opts);
        const { displayName, preferredNames, required, emptyByDefault, propertySection } = propertyOptions;
        return this.withPrimitiveProperty<T, WirePrimitiveValueWithType>(
            name,
            propertyOptions,
            { isSearchable: true, withFormat: true, withType: true, isDisplayed: true, preferredType: undefined },
            (
                _desc,
                _parentDesc,
                getIndirectTable,
                _schema,
                mutatingScreenKind,
                _isEditedInApp,
                invalidateForNewTable
            ) =>
                makeTextPropertyDescriptor(name, displayName, "", required, mutatingScreenKind, {
                    preferredType: "string",
                    preferredNames,
                    searchable: true,
                    columnFirst: true,
                    emptyByDefault,
                    propertySection,
                    getIndirectTable,
                    allowColumn: opts.allowColumn,
                    isEditedInApp: false,
                    isDefaultCaption: opts.isDefaultCaption,
                    invalidateForNewTable,
                })
        );
    }

    public withCaption<T extends string>(
        name: T,
        defaultCaption: string = startCase(name),
        opts: Partial<
            PropertyOptionsWithoutPreferredNames & WhenOption<TProps, TParent> & { readonly allowColumn: boolean }
        > = {}
    ) {
        const propertyOptions = ensurePropertyOptions(name, opts);
        const { displayName, required, emptyByDefault, propertySection } = propertyOptions;
        const preferredType = "string";
        return this.withPrimitiveProperty<T, string>(
            name,
            propertyOptions,
            { isSearchable: true, withFormat: true, withType: false, isDisplayed: false, preferredType },
            (
                _desc,
                _parentDesc,
                getIndirectTable,
                _schema,
                mutatingScreenKind,
                _isEditedInApp,
                invalidateForNewTable
            ) =>
                makeTextPropertyDescriptor(name, displayName, "", required, mutatingScreenKind, {
                    preferredType,
                    preferredNames: titleProperties,
                    searchable: false,
                    columnFirst: false,
                    emptyByDefault,
                    propertySection,
                    getIndirectTable,
                    allowColumn: opts.allowColumn,
                    isEditedInApp: false,
                    subcomponent: opts.subcomponent,
                    isCaption: defaultCaption,
                    invalidateForNewTable,
                })
        );
    }

    public withText<T extends string>(
        name: T,
        opts: Partial<
            PropertyOptions &
                WhenOption<TProps, TParent> & {
                    readonly allowColumn: boolean;
                    readonly isDefaultCaption: boolean;
                    readonly preferredType: StringGlideTypeKind;
                    readonly defaultValue: string;
                    readonly isMultiLine: boolean;
                    readonly syntaxMode: string;
                }
        > = {}
    ) {
        const propertyOptions = ensurePropertyOptions(name, opts);
        const { displayName, preferredNames, required, emptyByDefault, propertySection } = propertyOptions;
        const preferredType = opts.preferredType ?? "string";
        return this.withPrimitiveProperty<T, string>(
            name,
            propertyOptions,
            { isSearchable: true, withFormat: true, withType: false, isDisplayed: true, preferredType },
            (
                _desc,
                _parentDesc,
                getIndirectTable,
                _schema,
                mutatingScreenKind,
                _isEditedInApp,
                invalidateForNewTable
            ) =>
                makeTextPropertyDescriptor(name, displayName, "", required, mutatingScreenKind, {
                    preferredType,
                    preferredNames,
                    searchable: true,
                    defaultValue: opts.defaultValue,
                    columnFirst: opts.defaultValue === undefined,
                    emptyByDefault,
                    propertySection,
                    getIndirectTable,
                    allowColumn: opts.allowColumn,
                    isEditedInApp: false,
                    subcomponent: opts.subcomponent,
                    isMultiLine: opts.isMultiLine,
                    syntaxMode: opts.syntaxMode,
                    isDefaultCaption: opts.isDefaultCaption,
                    invalidateForNewTable,
                })
        );
    }

    public withURI<T extends string>(name: T, opts: Partial<PropertyOptions & WhenOption<TProps, TParent>> = {}) {
        const propertyOptions = ensurePropertyOptions(name, { preferredNames: ["url", "uri"], ...opts });
        const { displayName, preferredNames, required, emptyByDefault, propertySection } = propertyOptions;
        const preferredType = "uri";
        return this.withPrimitiveProperty<T, string>(
            name,
            propertyOptions,
            { isSearchable: false, withFormat: false, withType: false, isDisplayed: true, preferredType },
            (
                _desc,
                _parentDesc,
                getIndirectTable,
                _schema,
                mutatingScreenKind,
                _isEditedInApp,
                invalidateForNewTable
            ) =>
                makeTextPropertyDescriptor(name, displayName, "", required, mutatingScreenKind, {
                    preferredType,
                    preferredNames,
                    searchable: false,
                    columnFirst: true,
                    emptyByDefault,
                    getIndirectTable,
                    propertySection,
                    subcomponent: opts.subcomponent,
                    invalidateForNewTable,
                })
        );
    }

    public withImage<T extends string>(name: T, opts: Partial<PropertyOptions & WhenOption<TProps, TParent>> = {}) {
        const propertyOptions = ensurePropertyOptions(name, opts);
        const { displayName, required, emptyByDefault, propertySection } = propertyOptions;
        return this.withPrimitiveProperty<T, string>(
            name,
            propertyOptions,
            { isSearchable: false, withFormat: false, withType: false, isDisplayed: true, preferredType: "image-uri" },
            (
                _desc,
                _parentDesc,
                getIndirectTable,
                _schema,
                mutatingScreenKind,
                _isEditedInApp,
                invalidateForNewTable
            ) =>
                makeImagePropertyDescriptor(
                    name,
                    displayName,
                    "From URL",
                    "Enter URL",
                    required,
                    emptyByDefault,
                    false,
                    false,
                    false,
                    getIndirectTable,
                    mutatingScreenKind,
                    propertySection,
                    opts.subcomponent,
                    invalidateForNewTable
                )
        );
    }

    public withVideo<T extends string>(
        name: T,
        opts: Partial<PropertyOptions & WhenOption<TProps, TParent> & { readonly defaultValue: string }> = {}
    ) {
        const propertyOptions = ensurePropertyOptions(name, { preferredNames: ["video"], ...opts });
        const { displayName, required, emptyByDefault, propertySection } = propertyOptions;
        return this.withPrimitiveProperty<T, string>(
            name,
            propertyOptions,
            { isSearchable: false, withFormat: false, withType: false, isDisplayed: true, preferredType: "uri" },
            (
                _desc,
                _parentDesc,
                getIndirectTable,
                _schema,
                mutatingScreenKind,
                _isEditedInApp,
                invalidateForNewTable
            ) =>
                makeTextPropertyDescriptor(name, displayName, "Enter URL", required, mutatingScreenKind, {
                    preferredType: "uri",
                    preferredNames: ["video"],
                    searchable: false,
                    columnFirst: opts.defaultValue === undefined,
                    emptyByDefault,
                    defaultValue: opts.defaultValue,
                    getIndirectTable,
                    propertySection,
                    subcomponent: opts.subcomponent,
                    invalidateForNewTable,
                })
        );
    }

    public withEnum<T extends string, TEnum extends string>(
        name: T,
        values: WithEnumPropertyValuesConvenient<TEnum>,
        opts: Partial<
            PropertyOptions &
                WhenOption<TProps, TParent> & { defaultValue: TEnum; enumVisual?: EnumVisual; isSearchable?: boolean }
        > = {}
    ): FluentProperties<TProps & Record<T, TEnum>, TParent> {
        const { displayName, required, propertySection } = ensurePropertyOptions(name, opts);

        const enumValues = finalizeWithEnumPropertyValues(values);
        const { defaultValue = enumValues[0].value } = opts;

        const argumentSpec: ArgumentSpec = [name, "evaluable", required];
        const descr = new EnumPropertyHandler(
            { [name]: defaultValue },
            displayName,
            displayName,
            enumValues,
            propertySection ?? PropertySection.Options,
            opts.enumVisual ?? "dropdown",
            undefined,
            opts.subcomponent,
            opts.isSearchable ?? values.length > 4
        );
        const propertyDescriptorConstructor: PropertyDescriptorConstructor = () => descr;
        return this.withArgument(
            argumentSpec,
            propertyDescriptorConstructor,
            false,
            false,
            false,
            desc => [() => descr.getEnum(desc as any), { kind: "string" }, false],
            asMaybeString,
            undefined,
            false,
            opts.when
        );
    }

    public withSwitch<T extends string>(
        name: T,
        defaultValue: boolean,
        opts: Partial<PropertyOptions & WhenOption<TProps, TParent>> = {}
    ): FluentProperties<TProps & Record<T, boolean>, TParent> {
        const { displayName, propertySection } = ensurePropertyOptions(name, opts);

        const argumentSpec: ArgumentSpec = [name, "evaluable", true];
        const descr = new SwitchPropertyHandler(
            { [name]: defaultValue },
            displayName,
            propertySection ?? PropertySection.Options,
            opts.subcomponent
        );
        return this.withArgument(
            argumentSpec,
            () => descr,
            false,
            false,
            false,
            desc => [() => descr.getSwitch(desc as any), { kind: "boolean" }, false],
            asBoolean,
            undefined,
            false,
            opts.when
        );
    }

    public withEmoji<T extends string>(
        name: T,
        defaultEmoji?: string,
        opts: Partial<PropertyOptions & WhenOption<TProps, TParent> & { isDisplayed: boolean }> = {}
    ): FluentProperties<TProps & Partial<Record<T, string | undefined>>, TParent> {
        const { displayName, propertySection } = ensurePropertyOptions(name, opts);

        const argumentSpec: ArgumentSpec = [name, "evaluable", true];
        const descr: PropertyDescriptor = {
            kind: PropertyKind.Emoji,
            property: { name },
            label: displayName,
            defaultEmoji,
            section: propertySection ?? PropertySection.Options,
            subcomponent: opts.subcomponent,
        };
        return this.withArgument(
            argumentSpec,
            () => descr,
            false,
            false,
            false,
            desc => [() => getEmojiProperty((desc as any)[name]), { kind: "emoji" }, true],
            asString,
            undefined,
            opts.isDisplayed !== false,
            opts.when
        );
    }

    public withArray<T extends string, P>(
        name: T,
        props: FluentProperties<P, TProps>,
        opts: Partial<
            PropertyOptions & {
                readonly style: ArrayPropertyStyle;
                readonly specialItems: number;
                readonly allowEmpty: boolean;
                readonly removeIfMissing: Partial<Record<keyof P, readonly (keyof P)[]>>;
                readonly addItemLabels: readonly string[];
                readonly defaultValue: boolean | readonly Description[];
            }
        > = {}
    ): FluentProperties<TProps & Record<T, readonly P[]>, TParent> {
        const { displayName, propertySection } = ensurePropertyOptions(name, opts);

        const propertyDescriptorConstructor: PropertyDescriptorConstructor = (
            desc,
            _parentDesc,
            getIndirectTable,
            schema,
            mutatingScreenKind,
            isEditedInApp,
            invalidateForNewTable
        ) => ({
            kind: PropertyKind.Array,
            label: displayName,
            subcomponent: opts.subcomponent,
            property: { name },
            section: propertySection ?? PropertySection.Options,
            properties: [
                // TODO: get the array properties out of `desc` when we want
                // to support nested arrays
                ...makePropertyDescriptors(
                    undefined,
                    desc,
                    props.spec.propertySpecs,
                    getIndirectTable,
                    schema,
                    mutatingScreenKind,
                    isEditedInApp,
                    invalidateForNewTable
                ),
                ...makeActionDescriptors(
                    undefined,
                    desc,
                    props.spec.actionSpecs,
                    getIndirectTable,
                    schema,
                    mutatingScreenKind,
                    invalidateForNewTable
                ),
            ],
            specialItems: opts.specialItems,
            allowEmpty: opts.allowEmpty ?? false,
            allowReorder: true,
            addItemLabels: opts.addItemLabels ?? [],
            defaultValue: opts.defaultValue,
            style: opts.style ?? ArrayPropertyStyle.Default,
        });
        return new FluentProperties({
            ...this.spec,
            arraySpecs: [
                ...this.spec.arraySpecs,
                {
                    name,
                    descriptorConstructor: propertyDescriptorConstructor,
                    when: undefined,
                    itemsSpec: props.spec,
                    removeIfMissing: (opts.removeIfMissing as Record<string, readonly string[]>) ?? {},
                },
            ],
            hasDisplayedProperties: this.spec.hasDisplayedProperties || props.spec.hasDisplayedProperties,
        });
    }

    public withAction<T extends string>(
        name: T,
        opts: Partial<PropertyOptions & { readonly emptyByDefault: boolean }> = {}
    ): FluentProperties<TProps & Partial<Record<T, WireAction | Unbound | undefined>>, TParent> {
        const { displayName, required, propertySection } = ensurePropertyOptions(name, opts);
        const argumentSpec: ArgumentSpec = [name, "action", required];
        const descriptorConstructor: PropertyDescriptorConstructor = (
            _desc,
            _parentDesc,
            getIndirectTable,
            schema,
            mutatingScreenKind
        ) => {
            const kinds = getDefaultPrimitiveActionKinds(schema, mutatingScreenKind);
            return {
                kind: PropertyKind.Action,
                property: { name },
                label: displayName,
                required,
                kinds,
                defaultAction: opts.emptyByDefault === false ? true : required,
                getIndirectTable,
                section: propertySection ?? PropertySection.Action,
            };
        };
        return new FluentProperties({
            ...this.spec,
            actionSpecs: [...this.spec.actionSpecs, { argumentSpec, descriptorConstructor, when: undefined }],
        });
    }
}
